<script setup lang="ts">
definePageMeta({
  pageTransition: false
})
import { computed, onMounted, onUnmounted, ref, type ComponentPublicInstance, type Ref, watch } from 'vue'
import { useRoute, useRouter, onBeforeRouteUpdate } from 'vue-router'
import { listLifeArticle, type LifeArticleDTO, type ListLifeArticleResponse } from '@/composables/memorize/memorize'
import { useLifeListStore, type LifeDTO } from '@/composables/life/life'
import { handleErr } from '@/composables/exception/handler'
import type { CommonResponse } from '@/apis/api'
import { CODE_OK } from '@/apis/code'
import { ElMessage } from 'element-plus'
import { cutText, renderMessage, WAIT_FOR_LOADING_TIME } from '@/utils/util'
import { useServicesStore } from '@/composables/memorize/store'
import { draftStore } from '@/composables/memorize/draft'
import { isSetRealName } from '@/composables/user/users'
import SetRealNameDialog from '@/components/business/me/SetRealNameDialog.vue'
import { titleBar } from '@/composables/menu/title'
import { useI18n } from 'vue-i18n'
import { auditModeShow } from '@/utils/util'

const { t } = useI18n()
const realNameModalRef = ref<InstanceType<typeof SetRealNameDialog> | null>(null)

const route = useRoute()
const router = useRouter()

const serviceId = ref('')
serviceId.value = route.params.id as string

const lastId: Ref<string | null> = ref(null)
const pageSize = ref(15)
const total = ref(0)
const lifeArticles: Ref<Array<LifeArticleDTO>> = ref([])

const showFooter = ref(true)
const footerStatus = ref('none')
const hasMoreItems = ref(true)

const articleId = ref()
const located = ref(false)

const life: Ref<LifeDTO | undefined> = ref()
const lifeStore = useLifeListStore()

// SSR Data Fetching
const { data: ssrData } = await useAsyncData(`life-articles-${serviceId.value}`, async () => {
  const { data } = await listLifeArticle(serviceId.value, null, pageSize.value)
  // Ensure data is serializable and break any potential proxy chains
  return data.value ? JSON.parse(JSON.stringify(data.value)) : null
})

onMounted(() => {
  articleId.value = route.query.articleId as string
  if (articleId.value) {
    pageSize.value = 50
  }
  window.addEventListener('scroll', scrollListener)

  titleBar.value.category = 'none'

  life.value = lifeStore.searchByServiceId(serviceId.value)

  if (!ssrData.value || ssrData.value.code !== CODE_OK) {
    getData()
  }
})

const dataState = ref(ssrData.value && ssrData.value.code === CODE_OK ? 'done' : 'none')

function getData() {
  loadArticles(true)
  setTimeout(() => {
    if (dataState.value === 'none') {
      dataState.value = 'loading'
    }
  }, WAIT_FOR_LOADING_TIME)
}

onUnmounted(() => {
  window.removeEventListener('scroll', scrollListener)
})
const scrollListener = function () {
  // Get the current scroll position
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop
  // Get the maximum scroll height of the document
  const scrollHeight = document.documentElement.scrollHeight
  // Get the height of the viewport
  const clientHeight = document.documentElement.clientHeight
  // Check if the user has scrolled to the bottom
  if (scrollTop + clientHeight + 10 >= scrollHeight) {
    if (hasMoreItems.value && footerStatus.value !== 'loading') {
      loadArticles(false, lastId.value)
    }
  }
}

function tryAgain() {
  if (footerStatus.value === 'failed') {
    if (lifeArticles.value.length == 0) {
      loadArticles(true)
    } else {
      loadArticles(false, lastId.value)
    }
  }
}

async function loadArticles(isInitial: boolean = false, last: string | null = null) {
  if (footerStatus.value === 'loading') {
    return
  }

  if (isInitial) {
    lastId.value = null
  }

  footerStatus.value = 'loading'

  const { data, error, statusCode } = await listLifeArticle(serviceId.value, last, pageSize.value)
  if (error.value) {
    handleErr(data, error, statusCode)
    if (last === null) {
      dataState.value = 'error'
    } else {
      footerStatus.value = 'failed'
    }
  } else {
    const resData = data as unknown as Ref<CommonResponse<ListLifeArticleResponse>>
    if (resData.value.code === CODE_OK) {
      // success

      const response: ListLifeArticleResponse = resData.value.data

      if (last === null) {
        lifeArticles.value = response.items
        if (lifeArticles.value.length > 0) {
          dataState.value = 'done'
        } else {
          dataState.value = 'empty'
        }
      } else {
        lifeArticles.value.push(...response.items)
      }
      total.value = response.total
      footerStatus.value = 'none'
      hasMoreItems.value = true

      if (response.items.length === 0) {
        hasMoreItems.value = false
      } else {
        const newLastId = response.items[response.items.length - 1].id
        if (last !== null && newLastId === last) {
          hasMoreItems.value = false
        } else {
          lastId.value = newLastId
        }
      }

      if (articleId.value && !located.value) {
        toLocate()
      }
    } else {
      ElMessage({
        message: renderMessage(resData.value.message),
        type: 'warning'
      })
      if (last === null) {
        dataState.value = 'error'
      } else {
        footerStatus.value = 'failed'
      }
    }
  }
}

const playAnim = ref(false)

function toLocate() {
  setTimeout(() => {
    if (articleId.value && !located.value) {
      findAndScroll(articleId.value, 100)
    }
  }, 500)
}

const itemRefs: Map<string, HTMLElement> = new Map()

// Function to set references to the DOM elements
const setItemRef = (el: Element | ComponentPublicInstance | null, id: string): void => {
  // Ensure the ref is an HTMLElement
  let element: HTMLElement | null = null
  if (el && '$el' in el) {
    element = (el as ComponentPublicInstance).$el as HTMLElement
  } else if (el instanceof HTMLElement) {
    element = el
  }

  if (element) {
    itemRefs.set(id, element)
  } else {
    itemRefs.delete(id)
  }
}

const findAndScroll = (key: string, offset: number = 0) => {
  const element = itemRefs.get(key)
  if (element) {
    // Get the top position of the element relative to the viewport
    const elementTop = element.getBoundingClientRect().top
    // Calculate the absolute position to scroll to, considering the offset
    const scrollPosition = window.scrollY + elementTop - offset

    // Scroll to the calculated position
    window.scrollTo({
      top: scrollPosition,
      behavior: 'smooth'
    })
    located.value = true
    setTimeout(() => {
      playAnim.value = true
    }, 600)
  } else {
    console.log('Element not found:', itemRefs)
  }
}

function onDeleteArticle(articleId: string) {
  lifeArticles.value = lifeArticles.value.filter((item) => item.id !== articleId)
  if (lifeArticles.value.length == 0) {
    dataState.value = 'empty'
  }
  // 删除文章的草稿
  draftStore.value.deleteArticle(serviceId.value, articleId)
}

async function handleEdit(article: LifeArticleDTO) {
  if (!isSetRealName.value) {
    const isSuccess = await realNameModalRef.value?.open()
    if (!isSuccess) {
      console.log('用户取消了实名认证，停止后续操作')
      return
    }
  }
  router.push('/addOnesLife?serviceId=' + serviceId.value + '&isEdit=' + true + '&id=' + article.id)
}

function handleShare(article: LifeArticleDTO) {}

async function handleCreate() {
  console.log('13231312312')
  if (!isSetRealName.value) {
    const isSuccess = await realNameModalRef.value?.open()
    if (!isSuccess) {
      console.log('用户取消了实名认证，停止后续操作')
      return
    }
  }
  router.push('/addOnesLife?serviceId=' + serviceId.value + '&name=' + life.value?.name.fullName)
}

const serviceDetailsStore = useServicesStore()
const serviceDetails = computed(() => serviceDetailsStore.searchForItem(serviceId.value))

const canEdit = computed(() => {
  const permission = serviceDetails.value?.privilege?.permissions.find((item) => item.name === 'l_article')
  if (serviceDetails.value?.privilege == null) {
    return false
  } else {
    return permission?.value && permission.value !== 'READ'
  }
})

const canManage = computed(() => {
  const permission = serviceDetails.value?.privilege?.permissions.find((item) => item.name === 'l_article')
  if (serviceDetails.value?.privilege == null) {
    return false
  } else {
    return permission?.value && permission.value === 'MANAGE'
  }
})

function handleSsrData() {
  if (!ssrData.value) return
  const response = ssrData.value.data
  if (ssrData.value.code === CODE_OK && response) {
    lifeArticles.value = response.items
    if (lifeArticles.value.length > 0) {
      dataState.value = 'done'
    } else {
      dataState.value = 'empty'
    }
    total.value = response.total
    hasMoreItems.value = true
    if (response.items.length === 0) {
      hasMoreItems.value = false
    } else {
      const lastItem = lifeArticles.value[lifeArticles.value.length - 1]
      if (lastItem) {
        lastId.value = lastItem.id
      }
    }

    if (articleId.value && !located.value) {
      toLocate()
    }
  }
}

if (ssrData.value && ssrData.value.code === CODE_OK) {
  handleSsrData()
}

const pageTitle = computed(() => {
  const fullName = serviceDetails.value?.life?.name?.fullName
  if (auditModeShow.value) {
    return t('m.tag_title_audit', { fullName: fullName })
  } else if (inMiniprograme.value) {
    return t('m.tag_title_mini', { fullName: fullName })
  }
  return fullName ? t('m.memorial_hall_title', { name: fullName }) : t('m.memorial_hall_default')
})

const requestURL = useRequestURL()
const canonicalUrl = computed(() => `${requestURL.origin}${route.path}`)

useSeoMeta({
  title: pageTitle,
  ogTitle: pageTitle,
  description: () => t('m.life_desc', { name: serviceDetails.value?.life?.name?.fullName }),
  ogDescription: () => t('m.life_desc', { name: serviceDetails.value?.life?.name?.fullName }),
  ogImage: () => serviceDetails.value?.life?.avatar?.url || serviceDetails.value?.life?.avatar?.middleQuality,
  ogUrl: canonicalUrl,
  twitterCard: 'summary_large_image'
})

useHead({
  link: [
    {
      rel: 'canonical',
      href: canonicalUrl
    }
  ]
})
</script>
<template>
  <BaseLayout :state="dataState" @retry="getData">
    <div w-full flex="~ col" px-1 pt-1>
      <div w-full flex="~ row" justify-end>
        <ClientOnly>
          <div v-if="lifeArticles.length > 0 && canEdit">
            <RActionButton
              mt-6
              :title="$t('action.add_ones_life')"
              name="add_ones_life"
              titleClass="md:text-theme-main"
              :serviceId="serviceId">
              <div i-ri:edit-line text-lg mr-2 class="md:text-theme-main"></div>
            </RActionButton>
          </div>
        </ClientOnly>
      </div>
      <LifeArticle
        v-for="item in lifeArticles"
        :key="item.id"
        :article="item"
        :ref="(el: Element | ComponentPublicInstance | null) => setItemRef(el, item.id)"
        :anim="playAnim && item.id == articleId"
        :canEdit="canEdit"
        :canManage="canManage"
        @animationEnd="playAnim = false"
        @onDelete="onDeleteArticle"
        @onEdit="handleEdit(item)"
        @onShare="handleShare(item)">
      </LifeArticle>
      <MFooter v-model="showFooter" :loading="footerStatus" :hasMore="hasMoreItems" @click="tryAgain" />
    </div>
    <template #skeleton>
      <LifeArticleSkeleton />
    </template>
    <template #empty>
      <EmptyView :msg="canEdit ? $t('empty.no_life_article') : $t('m.slide_no_life')" :theme="true">
        <ClientOnly>
          <div v-if="canEdit">
            <button btn-theme-outline bg-transparent rounded-full px-8 mt-20 @click="handleCreate">
              {{ $t('action.add_ones_life') }}
            </button>
          </div>
        </ClientOnly>
      </EmptyView>
    </template>
    <ClientOnly>
      <div>
        <SetRealNameDialog ref="realNameModalRef" />
      </div>
    </ClientOnly>
  </BaseLayout>
</template>
<style lang="scss" scoped></style>
